import { WINDOW_LEVEL_PRESETS, getWindowLevelPreset } from './window-level-presets';
import type { WindowLevelPresetId } from './window-level-presets';
import { PubSubService } from '../core/pubsub-service';
import type { ServiceContext, ServiceRegistration } from '../core/types';

export const DEFAULT_COLOR_WINDOW = 800;
export const DEFAULT_COLOR_LEVEL = 200;

export interface WindowLevel {
    window: number;
    level: number;
}

export class WindowLevelService extends PubSubService {
    static readonly SERVICE_NAME = 'WindowLevelService';

    static readonly REGISTRATION: ServiceRegistration = {
        name: WindowLevelService.SERVICE_NAME,
        factory: (_context: ServiceContext) => new WindowLevelService(),
    };

    readonly EVENTS = {
        WINDOW_LEVEL_CHANGED: 'windowlevel:changed',
    } as const;

    private _window: number = DEFAULT_COLOR_WINDOW;
    private _level: number = DEFAULT_COLOR_LEVEL;

    /** Revert point captured by startPreview(); null when no preview is active. */
    private _previewBase: WindowLevel | null = null;

    /** Workflow-configured default preset applied by resetToDefaults(); null = hardcoded fallback. */
    private _defaultPresetId: WindowLevelPresetId | null = null;

    get window(): number {
        return this._window;
    }

    get level(): number {
        return this._level;
    }

    getWindowLevel(): WindowLevel {
        return { window: this._window, level: this._level };
    }

    setWindowLevel(window: number, level: number): void {
        // Clamp window to minimum of 1 (avoid zero-width or inverted range)
        const clampedWindow = Math.max(1, window);

        if (clampedWindow === this._window && level === this._level) return;

        this._window = clampedWindow;
        this._level = level;

        this.broadcast(this.EVENTS.WINDOW_LEVEL_CHANGED, this.getWindowLevel());
    }

    // ========================================================================
    // Presets
    // ========================================================================

    /**
     * Apply a preset by id (broadcasts WINDOW_LEVEL_CHANGED → viewports re-render).
     * Unknown ids are ignored — config ids are validated at definition time, so this
     * only guards against programming errors, never crashes rendering.
     */
    applyPreset(presetId: WindowLevelPresetId): void {
        const preset = getWindowLevelPreset(presetId);
        if (!preset) {
            console.warn(`[WindowLevelService] Unknown preset id: "${presetId}"`);
            return;
        }
        this.setWindowLevel(preset.window, preset.level);
    }

    /** Id of the preset whose values exactly match the live W/L, or null (any drag breaks the match). */
    getActivePresetId(): WindowLevelPresetId | null {
        return WindowLevelService.matchPresetId(this._window, this._level);
    }

    /** The W/L that resetToDefaults() applies: the workflow default preset, or the hardcoded fallback. */
    getDefaultWindowLevel(): WindowLevel {
        const preset = this._defaultPresetId ? getWindowLevelPreset(this._defaultPresetId) : null;
        if (preset) return { window: preset.window, level: preset.level };
        return { window: DEFAULT_COLOR_WINDOW, level: DEFAULT_COLOR_LEVEL };
    }

    /** Configure the per-workflow default preset applied by resetToDefaults() (null = hardcoded fallback). */
    configureDefaultPreset(id: WindowLevelPresetId | null): void {
        this._defaultPresetId = id;
    }

    private static matchPresetId(window: number, level: number): WindowLevelPresetId | null {
        return WINDOW_LEVEL_PRESETS.find((p) => p.window === window && p.level === level)?.id ?? null;
    }

    // ========================================================================
    // Preview / commit (hover-preview semantics for the toolbar menu)
    // ========================================================================

    /** Snapshot the current W/L as the revert point. Idempotent while a preview is active. */
    startPreview(): void {
        if (this._previewBase) return;
        this._previewBase = this.getWindowLevel();
    }

    /** Apply a candidate W/L during preview (same broadcast path as a committed change). */
    previewWindowLevel(window: number, level: number): void {
        this.setWindowLevel(window, level);
    }

    /** Re-apply the revert point but keep previewing (used when the pointer leaves a menu item). */
    revertPreview(): void {
        if (!this._previewBase) return;
        this.setWindowLevel(this._previewBase.window, this._previewBase.level);
    }

    /** Revert to the snapshot and end the preview (menu closed without a selection). */
    cancelPreview(): void {
        if (!this._previewBase) return;
        const base = this._previewBase;
        this._previewBase = null;
        this.setWindowLevel(base.window, base.level);
    }

    /** Keep the current (previewed) value and clear the revert point. */
    commitPreview(): void {
        this._previewBase = null;
    }

    resetToDefaults(): void {
        const { window, level } = this.getDefaultWindowLevel();
        this.setWindowLevel(window, level);
    }

    override reset(): void {
        this._window = DEFAULT_COLOR_WINDOW;
        this._level = DEFAULT_COLOR_LEVEL;
        this._previewBase = null;
        this._defaultPresetId = null;
        super.reset();
    }
}
